import {
  Avatar,
  Box,
  Card,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  convertToFullName,
  getAmbassadorAvatarSrc,
} from "../../../../services/utils";

type SuggestedAmbassadorCardProps = {
  ambassador: any;
  selected: boolean;
  disabled?: boolean;
  onToggle: (ambassador: any, checked: boolean) => void;
};

function getAmbassadorId(ambassador) {
  return ambassador?.id || ambassador?._id;
}

export default function SuggestedAmbassadorCard({
  ambassador,
  selected,
  disabled,
  onToggle,
}: SuggestedAmbassadorCardProps) {
  const fullName = convertToFullName(
    ambassador?.firstName,
    ambassador?.lastName
  );
  const matchReason = ambassador?.matchReason;
  const matchScore = ambassador?.matchScore;
  const avatarSrc = getAmbassadorAvatarSrc(ambassador);

  return (
    <Card
      variant="outlined"
      onClick={() => !disabled && onToggle(ambassador, !selected)}
      sx={{
        height: "100%",
        minHeight: { xs: 160, md: 172, lg: 188 },
        borderRadius: 2,
        borderColor: selected ? "#3B82F6" : "#E5E7EB",
        borderLeftWidth: selected ? 4 : 1,
        borderLeftColor: selected ? "#3B82F6" : "#E5E7EB",
        bgcolor: selected ? "#F8FAFF" : "#FFFFFF",
        boxShadow: selected
          ? "0 2px 8px rgba(59, 130, 246, 0.12)"
          : "none",
        cursor: disabled ? "default" : "pointer",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        "&:hover": disabled
          ? {}
          : {
              borderColor: "#93C5FD",
              bgcolor: selected ? "#F8FAFF" : "#FAFAFA",
            },
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={{ xs: 1.5, md: 1.25 }}
        px={{ xs: 1.5, md: 1.5 }}
        py={{ xs: 1.75, md: 2 }}
        height="100%"
        minHeight="inherit"
      >
        <Avatar
          src={avatarSrc}
          alt={fullName}
          imgProps={{
            style: { objectFit: "cover", width: "100%", height: "100%" },
          }}
          sx={{
            width: { xs: 64, md: 56 },
            height: { xs: 64, md: 56 },
            flexShrink: 0,
            bgcolor: "#070639",
            fontSize: { xs: 24, md: 20 },
            fontWeight: 700,
            border: "1px solid #E5E7EB",
          }}
        >
          {ambassador?.firstName?.[0]}
        </Avatar>

        <Box
          flex={1}
          minWidth={0}
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <Box display="flex" alignItems="flex-start" gap={0.5}>
            <Box flex={1} minWidth={0}>
              <Tooltip title={fullName || ""} arrow>
                <Typography
                  fontWeight={700}
                  fontSize={{ xs: 15, md: 14 }}
                  color="#1F2937"
                  noWrap
                >
                  {fullName || "N/A"}
                </Typography>
              </Tooltip>
              {ambassador?.title && (
                <Typography
                  fontSize={{ xs: 13, md: 12 }}
                  color="#6B7280"
                  mt={0.25}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {ambassador.title}
                </Typography>
              )}
            </Box>
            <Checkbox
              checked={selected}
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggle(ambassador, e.target.checked)}
              sx={{
                p: 0,
                mt: -0.5,
                flexShrink: 0,
                color: "#9CA3AF",
                "&.Mui-checked": { color: "#3B82F6" },
              }}
              inputProps={{
                "aria-label": `Select ${fullName || "ambassador"}`,
              }}
            />
          </Box>

          <Stack spacing={0.35} mt={0.75}>
            {ambassador?.company && (
              <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <BusinessOutlinedIcon
                  sx={{ fontSize: 14, color: "#4B5563", flexShrink: 0 }}
                />
                <Typography fontSize={11} fontWeight={500} color="#111827" noWrap>
                  {ambassador.company}
                </Typography>
              </Box>
            )}
            {ambassador?.no_of_emp && (
              <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <GroupsOutlinedIcon
                  sx={{ fontSize: 14, color: "#4B5563", flexShrink: 0 }}
                />
                <Typography fontSize={11} fontWeight={500} color="#111827" noWrap>
                  {ambassador.no_of_emp}
                </Typography>
              </Box>
            )}
          </Stack>

          {matchReason && (
            <Box mt="auto" pt={2} flex={1} minWidth={0}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                mb={0.5}
              >
                <Typography
                  fontSize={10}
                  fontWeight={600}
                  color="#000000"
                  textTransform="uppercase"
                  letterSpacing="0.04em"
                >
                  Why This Match?
                </Typography>
                {matchScore != null && (
                  <Chip
                    size="small"
                    icon={<AutoAwesomeIcon sx={{ fontSize: 13 }} />}
                    label={`Matching Score ${matchScore}`}
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: "#EEF2FF",
                      color: "#1F2937",
                      "& .MuiChip-icon": { color: "#4F46E5", ml: 0.5 },
                    }}
                  />
                )}
              </Box>
              <Tooltip title={matchReason} arrow placement="top">
                <Typography
                  fontSize={11}
                  color="#374151"
                  lineHeight={1.5}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: { xs: 4, lg: 5 },
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {matchReason}
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

export { getAmbassadorId };
